import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const RiskContext = createContext();

const MATRIX_DEFAULTS = {
  likelihoodLevels: ['Very Low', 'Low', 'Medium', 'High', 'Very High'],
  impactLevels: ['Very Low', 'Low', 'Medium', 'High', 'Very High'],
  calculationType: 'linear',
  scoreThresholds: [
    { value: 5, label: 'Very Low', color: '#22c55e' },
    { value: 10, label: 'Low', color: '#84cc16' },
    { value: 15, label: 'Medium', color: '#eab308' },
    { value: 20, label: 'High', color: '#f97316' },
    { value: 25, label: 'Critical', color: '#ef4444' }
  ]
};

export function RiskProvider({ children }) {
  const [risks, setRisks] = useState([
    { 
      id: 1, 
      description: "Key team member leaves", 
      initialLikelihood: 2,
      initialImpact: 4,
      likelihood: 2, 
      impact: 4, 
      category: "Resource", 
      action: "Mitigate",
      actionDescription: "Cross-train team members and create documentation",
      actionOwner: "HR Manager",
      deadline: "2025-07-15",
      status: "In Progress"
    },
    { 
      id: 2, 
      description: "Budget overrun", 
      initialLikelihood: 3,
      initialImpact: 3,
      likelihood: 3, 
      impact: 3, 
      category: "Financial", 
      action: "Monitor",
      actionDescription: "Weekly budget tracking and variance reports",
      actionOwner: "Finance Lead",
      deadline: "2025-07-30",
      status: "Not Started"
    },
    { 
      id: 3, 
      description: "Client changes requirements", 
      initialLikelihood: 4,
      initialImpact: 3,
      likelihood: 4, 
      impact: 3, 
      category: "Scope", 
      action: "Accept",
      actionDescription: "Establish change control process",
      actionOwner: "Project Manager",
      deadline: "2025-07-10",
      status: "Completed"
    }
  ]);

  const [editingRisk, setEditingRisk] = useState(null);
  const [showRiskDetail, setShowRiskDetail] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    action: '',
    owner: '',
    deadline: ''
  });
  const [matrixConfig, setMatrixConfig] = useState({ ...MATRIX_DEFAULTS });
  const [kanbanGrouping, setKanbanGrouping] = useState('Status');
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'riskScore', direction: 'desc' });
  const [sampleDataSet, setSampleDataSet] = useState('8');
  const [draggedRisk, setDraggedRisk] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Categories state (CRUD capable)
  const [categories, setCategories] = useState(['Technical', 'Financial', 'Resource', 'Scope', 'External', 'Quality']);

  const addCategory = (label) => {
    if (!label) return;
    setCategories(prev => {
      if (prev.includes(label)) return prev;
      return [...prev, label];
    });
  };

  const updateCategory = (oldLabel, newLabel) => {
    if (!oldLabel || !newLabel) return;
    setCategories(prev => prev.map(cat => (cat === oldLabel ? newLabel : cat)));
    // Update existing risks that use the old category label
    setRisks(prev => prev.map(risk => risk.category === oldLabel ? { ...risk, category: newLabel } : risk));
  };

  const deleteCategory = (label) => {
    setCategories(prev => prev.filter(cat => cat !== label));
    // Optional: update risks with deleted category to "Uncategorized"
    setRisks(prev => prev.map(risk => risk.category === label ? { ...risk, category: '' } : risk));
  };

  // Constants
  const [riskActions, setRiskActions] = useState(['Monitor', 'Mitigate', 'Transfer', 'Accept', 'Avoid', 'Escalate']);
  const [statusOptions, setStatusOptions] = useState(['Not Started', 'In Progress', 'Completed', 'On Hold', 'Overdue']);
  
  const actionColors = {
    'Monitor': 'bg-blue-100 text-blue-800 border-blue-200',
    'Mitigate': 'bg-green-100 text-green-800 border-green-200',
    'Transfer': 'bg-purple-100 text-purple-800 border-purple-200',
    'Accept': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Avoid': 'bg-red-100 text-red-800 border-red-200',
    'Escalate': 'bg-orange-100 text-orange-800 border-orange-200'
  };
  
  const categoryColors = {
    'Technical': 'bg-blue-500',
    'Financial': 'bg-green-500',
    'Resource': 'bg-purple-500',
    'Scope': 'bg-orange-500',
    'External': 'bg-red-500',
    'Quality': 'bg-yellow-500'
  };

  const kanbanFieldMap = {
    'Status': 'status',
    'Category': 'category',
    'Action': 'action',
    'Owner': 'actionOwner'
  };

  // Helper functions
  const updateRisk = (riskId, field, value) => {
    setRisks(risks.map(risk => 
      risk.id === riskId ? { ...risk, [field]: value } : risk
    ));
  };

  const removeRisk = (id) => {
    setRisks(risks.filter(risk => risk.id !== id));
  };

  const getRiskPriority = (likelihood, impact) => {
    if (!matrixConfig.scoreThresholds || !Array.isArray(matrixConfig.scoreThresholds)) {
      return { level: 'Medium', color: '#eab308' };
    }
    const sorted = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
    let score;
    if (matrixConfig.calculationType === 'logarithmic') {
      score = Math.log10(likelihood * impact);
    } else {
      score = likelihood * impact;
    }
    for (const threshold of sorted) {
      if (score <= threshold.value) {
        return { level: threshold.label, color: threshold.color || '#eab308' };
      }
    }
    return { level: sorted[sorted.length - 1]?.label || 'Critical', color: sorted[sorted.length - 1]?.color || '#ef4444' };
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      category: '',
      action: '',
      owner: '',
      deadline: ''
    });
  };

  const filteredRisks = useMemo(() => {
    return risks.filter(risk => {
      if (filters.status && risk.status !== filters.status) return false;
      if (filters.category && risk.category !== filters.category) return false;
      if (filters.action && risk.action !== filters.action) return false;
      if (filters.owner && risk.actionOwner !== filters.owner) return false;
      if (filters.deadline) {
        const today = new Date();
        const riskDeadline = risk.deadline ? new Date(risk.deadline) : null;
        
        if (filters.deadline === 'overdue' && (!riskDeadline || riskDeadline >= today)) return false;
        if (filters.deadline === 'thisWeek') {
          if (!riskDeadline) return false;
          const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          if (riskDeadline < today || riskDeadline > weekFromNow) return false;
        }
        if (filters.deadline === 'noDeadline' && riskDeadline) return false;
      }
      return true;
    });
  }, [risks, filters]);

  const getUniqueOwners = () => {
    return [...new Set(risks.map(risk => risk.actionOwner).filter(Boolean))];
  };

  const getKanbanColumns = () => {
    switch (kanbanGrouping) {
      case 'Status':
        return statusOptions;
      case 'Category':
        return categories;
      case 'Action':
        return riskActions;
      case 'Owner':
        return getUniqueOwners().length > 0 ? getUniqueOwners() : ['Unassigned'];
      default:
        return statusOptions;
    }
  };

  const openRiskDetail = (risk) => {
    setEditingRisk(risk);
    setShowRiskDetail(true);
  };

  const closeRiskDetail = () => {
    setShowRiskDetail(false);
    setEditingRisk(null);
  };

  const handleEditRiskChange = (field, value) => {
    if (editingRisk) {
      const updatedRisk = { ...editingRisk, [field]: value };
      setEditingRisk(updatedRisk);
    }
  };

  const saveRiskChanges = () => {
    if (editingRisk) {
      setRisks(prevRisks => {
        const riskExists = prevRisks.some(r => r.id === editingRisk.id);
        
        if (riskExists) {
          // Update existing risk
          return prevRisks.map(risk =>
            risk.id === editingRisk.id ? { ...editingRisk } : risk
          );
        } else {
          // Add new risk
          const newId = prevRisks.length > 0 ? Math.max(...prevRisks.map(r => r.id)) + 1 : 1;
          return [...prevRisks, { ...editingRisk, id: newId }];
        }
      });
      closeRiskDetail();
    }
  };

  const getSortedRisks = useMemo(() => {
    const sorted = [...filteredRisks].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'riskScore') {
        const aPriority = getRiskPriority(a.likelihood, a.impact);
        const bPriority = getRiskPriority(b.likelihood, b.impact);
        const scoreMap = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1, 'Very Low': 0 };
        aValue = scoreMap[aPriority.level] || 0;
        bValue = scoreMap[bPriority.level] || 0;
      } else if (sortConfig.key === 'deadline') {
        aValue = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
        bValue = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
      } else if (sortConfig.key === 'likelihood' || sortConfig.key === 'impact') {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      } else {
        aValue = a[sortConfig.key]?.toString().toLowerCase() || '';
        bValue = b[sortConfig.key]?.toString().toLowerCase() || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      
      if (sortConfig.key !== 'deadline') {
        const aDeadline = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
        const bDeadline = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
        return aDeadline - bDeadline;
      }
      
      return 0;
    });
    
    return sorted;
  }, [filteredRisks, sortConfig, getRiskPriority]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortConfig.direction === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  const startEditing = (riskId, field, currentValue) => {
    setEditingCell({ riskId, field });
    setEditingValue(currentValue || '');
  };

  const saveInlineEdit = () => {
    if (editingCell) {
      updateRisk(editingCell.riskId, editingCell.field, editingValue);
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleInlineKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveInlineEdit();
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  // Load sample datasets (3, 8, or 20 records)
  const getSampleDataSet = (setSize) => {
    const smallSet = [
      { id: 1, description: 'Budget overrun', likelihood: 3, impact: 4, category: 'Financial', action: 'Monitor', actionDescription: 'Weekly budget tracking', actionOwner: 'Finance Lead', deadline: '2025-07-30', status: 'Not Started' },
      { id: 2, description: 'Key team member leaves', likelihood: 2, impact: 4, category: 'Resource', action: 'Mitigate', actionDescription: 'Cross-train team members', actionOwner: 'HR Manager', deadline: '2025-07-15', status: 'In Progress' },
      { id: 3, description: 'Client changes requirements', likelihood: 4, impact: 3, category: 'Scope', action: 'Accept', actionDescription: 'Change control process', actionOwner: 'Project Manager', deadline: '2025-07-10', status: 'Completed' }
    ];

    const mediumSet = [
      { id: 1, description: 'Key team member leaves', likelihood: 2, impact: 4, category: 'Resource', action: 'Mitigate', actionDescription: 'Cross-train team members and create documentation', actionOwner: 'HR Manager', deadline: '2025-07-15', status: 'In Progress' },
      { id: 2, description: 'Budget overrun', likelihood: 4, impact: 4, category: 'Financial', action: 'Avoid', actionDescription: 'Add contingency budget', actionOwner: 'Finance Lead', deadline: '2025-08-01', status: 'On Hold' },
      { id: 3, description: 'Client changes requirements mid-project', likelihood: 5, impact: 5, category: 'Scope', action: 'Escalate', actionDescription: 'Formal change board approvals', actionOwner: 'Project Sponsor', deadline: '2025-07-20', status: 'Not Started' },
      { id: 4, description: 'Technology stack becomes obsolete', likelihood: 3, impact: 3, category: 'Technical', action: 'Monitor', actionDescription: 'Quarterly tech assessment', actionOwner: 'Tech Lead', deadline: '2025-09-15', status: 'In Progress' },
      { id: 5, description: 'Vendor delays delivery', likelihood: 3, impact: 2, category: 'External', action: 'Transfer', actionDescription: 'Penalty clauses in contracts', actionOwner: 'Procurement', deadline: '2025-07-05', status: 'Overdue' },
      { id: 6, description: 'Quality standards not met', likelihood: 2, impact: 2, category: 'Quality', action: 'Mitigate', actionDescription: 'Peer review process', actionOwner: 'QA Manager', deadline: '2025-07-20', status: 'In Progress' },
      { id: 7, description: 'Data breach or security incident', likelihood: 2, impact: 5, category: 'External', action: 'Avoid', actionDescription: 'MFA and audits', actionOwner: 'Security Lead', deadline: '2025-07-25', status: 'Not Started' },
      { id: 8, description: 'Critical hardware failure', likelihood: 1, impact: 4, category: 'Technical', action: 'Mitigate', actionDescription: 'Redundant hardware', actionOwner: 'Infra Team', deadline: '2025-08-10', status: 'Completed' }
    ];

    const largeSet = [...mediumSet,
      { id: 9, description: 'Database performance degradation', likelihood: 3, impact: 3, category: 'Technical', action: 'Monitor', actionDescription: 'Performance optimisation', actionOwner: 'DBA', deadline: '2025-08-15', status: 'Not Started' },
      { id: 10, description: 'Regulatory compliance changes', likelihood: 4, impact: 4, category: 'External', action: 'Monitor', actionDescription: 'Track updates', actionOwner: 'Compliance Officer', deadline: '2025-09-01', status: 'In Progress' },
      { id: 11, description: 'Third-party API changes', likelihood: 3, impact: 3, category: 'Technical', action: 'Monitor', actionDescription: 'Monitor deprecation notices', actionOwner: 'Tech Lead', deadline: '2025-08-20', status: 'Not Started' },
      { id: 12, description: 'Market conditions affect project viability', likelihood: 2, impact: 5, category: 'External', action: 'Accept', actionDescription: 'Market analysis', actionOwner: 'Business Analyst', deadline: '2025-09-30', status: 'Not Started' },
      { id: 13, description: 'Scope creep without budget increase', likelihood: 4, impact: 3, category: 'Scope', action: 'Mitigate', actionDescription: 'Strict change control', actionOwner: 'Project Manager', deadline: '2025-07-25', status: 'In Progress' },
      { id: 14, description: 'Communication breakdown between teams', likelihood: 3, impact: 3, category: 'Resource', action: 'Mitigate', actionDescription: 'Cross-team meetings', actionOwner: 'Scrum Master', deadline: '2025-07-12', status: 'Completed' },
      { id: 15, description: 'Testing phase delays', likelihood: 3, impact: 4, category: 'Quality', action: 'Mitigate', actionDescription: 'Parallel testing', actionOwner: 'QA Manager', deadline: '2025-08-05', status: 'In Progress' },
      { id: 16, description: 'Integration issues with legacy systems', likelihood: 4, impact: 4, category: 'Technical', action: 'Mitigate', actionDescription: 'Early integration testing', actionOwner: 'Integration Lead', deadline: '2025-08-12', status: 'Not Started' },
      { id: 17, description: 'Lack of user adoption', likelihood: 2, impact: 4, category: 'External', action: 'Mitigate', actionDescription: 'Training program', actionOwner: 'Change Manager', deadline: '2025-09-20', status: 'On Hold' },
      { id: 18, description: 'Performance bottlenecks in production', likelihood: 3, impact: 4, category: 'Technical', action: 'Mitigate', actionDescription: 'Load testing', actionOwner: 'Performance Engineer', deadline: '2025-08-25', status: 'Not Started' },
      { id: 19, description: 'Intellectual property disputes', likelihood: 1, impact: 5, category: 'External', action: 'Transfer', actionDescription: 'Legal review', actionOwner: 'Legal Team', deadline: '2025-07-18', status: 'Overdue' },
      { id: 20, description: 'Skills gap in emerging technologies', likelihood: 3, impact: 3, category: 'Resource', action: 'Mitigate', actionDescription: 'Training', actionOwner: 'Training Manager', deadline: '2025-08-30', status: 'In Progress' }
    ];

    switch (setSize) {
      case '3': return smallSet;
      case '8': return mediumSet;
      case '20': return largeSet;
      default: return mediumSet;
    }
  };

  const loadSampleData = useCallback(() => {
    const sizes = ['3', '8', '20'];
    const currentIndex = sizes.indexOf(sampleDataSet);
    const nextSize = sizes[(currentIndex + 1) % sizes.length];
    setSampleDataSet(nextSize);

    let sampleRisks = getSampleDataSet(nextSize).map(r => ({
      ...r,
      initialLikelihood: r.initialLikelihood ?? r.likelihood ?? r.probability ?? 1,
      initialImpact: r.initialImpact ?? r.impact ?? 1,
      likelihood: r.likelihood ?? r.probability ?? 1,
      impact: r.impact ?? 1
    }));
    // Rank by (likelihood * impact) descending, then assign sequential IDs
    sampleRisks = sampleRisks
      .sort((a,b)=> (b.likelihood*b.impact) - (a.likelihood*a.impact))
      .map((r,idx)=>({...r,id: idx+1}));
    setRisks(sampleRisks);
  }, [sampleDataSet]);

  const clearAllData = () => {
    setRisks([]);
    clearFilters(); // Also clear any active filters
  };

  // CRUD for Actions
  const addAction = (label) => {
    if (!label) return;
    setRiskActions(prev => (prev.includes(label) ? prev : [...prev, label]));
  };
  const updateAction = (oldLabel, newLabel) => {
    if (!oldLabel || !newLabel) return;
    setRiskActions(prev => prev.map(a => (a === oldLabel ? newLabel : a)));
    setRisks(prev => prev.map(r => r.action === oldLabel ? { ...r, action: newLabel } : r));
  };
  const deleteAction = (label) => {
    setRiskActions(prev => prev.filter(a => a !== label));
    setRisks(prev => prev.map(r => r.action === label ? { ...r, action: '' } : r));
  };

  // CRUD for Status
  const addStatus = (label) => {
    if (!label) return;
    setStatusOptions(prev => (prev.includes(label) ? prev : [...prev, label]));
  };
  const updateStatus = (oldLabel, newLabel) => {
    if (!oldLabel || !newLabel) return;
    setStatusOptions(prev => prev.map(s => (s === oldLabel ? newLabel : s)));
    setRisks(prev => prev.map(r => r.status === oldLabel ? { ...r, status: newLabel } : r));
  };
  const deleteStatus = (label) => {
    setStatusOptions(prev => prev.filter(s => s !== label));
    setRisks(prev => prev.map(r => r.status === label ? { ...r, status: '' } : r));
  };

  // Add Risk modal
  const handleAddRiskModal = () => {
    const nextId = risks.reduce((max, r) => Math.max(max, r.id), 0) + 1;
    setEditingRisk({
      id: nextId,
      description: '',
      initialLikelihood: 1,
      initialImpact: 1,
      likelihood: 1,
      impact: 1,
      category: categories[0] || '',
      action: riskActions[0] || '',
      actionDescription: '',
      actionOwner: '',
      deadline: '',
      status: statusOptions[0] || ''
    });
    setShowRiskDetail(true);
  };

  // Export helpers
  const exportToSpreadsheet = () => {
    const ws = XLSX.utils.json_to_sheet(risks);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Risks');
    XLSX.writeFile(wb, 'risk-register.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Helper function to convert hex color to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 34, g: 197, b: 94 }; // default green
    };

    // --- PAGE 1: Risk Impact Matrix ---
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Impact Matrix', 14, 20);
    
    // Matrix configuration
    const matrixStartX = 14;
    const matrixStartY = 35;
    const cellSize = 20;
    const headerSize = 15;
    const impactLabels = matrixConfig.impactLevels;
    const likelihoodLabels = [...matrixConfig.likelihoodLevels].reverse(); // reverse for display
    
    // Draw matrix title labels
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Impact →', matrixStartX + headerSize + (impactLabels.length * cellSize) / 2 - 10, matrixStartY - 8);
    doc.text('Likelihood ↓', matrixStartX - 5, matrixStartY - 8);
    
    // Draw column headers (Impact)
    doc.setFontSize(8);
    impactLabels.forEach((label, idx) => {
      const x = matrixStartX + headerSize + (idx * cellSize);
      const y = matrixStartY + headerSize - 3;
      doc.setFillColor(240, 240, 240);
      doc.rect(x, matrixStartY, cellSize, headerSize, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      doc.text(label.substring(0, 8), x + cellSize/2, y, { align: 'center' });
    });
    
    // Draw row headers (Likelihood) and matrix cells
    likelihoodLabels.forEach((label, rowIdx) => {
      const y = matrixStartY + headerSize + (rowIdx * cellSize);
      
      // Row header
      doc.setFillColor(240, 240, 240);
      doc.rect(matrixStartX, y, headerSize, cellSize, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      doc.text(label.substring(0, 8), matrixStartX + headerSize/2, y + cellSize/2 + 2, { align: 'center' });
      
      // Matrix cells
      impactLabels.forEach((_, colIdx) => {
        const cellX = matrixStartX + headerSize + (colIdx * cellSize);
        const cellY = y;
        
        // Calculate score and color for this cell
        const likelihood = matrixConfig.likelihoodLevels.length - rowIdx;
        const impact = colIdx + 1;
        let score;
        if (matrixConfig.calculationType === 'logarithmic') {
          score = Math.log10(likelihood * impact);
        } else {
          score = likelihood * impact;
        }
        
        // Find matching threshold color
        const sortedThresholds = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
        let cellColor = '#22c55e';
        for (const threshold of sortedThresholds) {
          if (score <= threshold.value) {
            cellColor = threshold.color || '#22c55e';
            break;
          }
        }
        if (score > sortedThresholds[sortedThresholds.length - 1]?.value) {
          cellColor = sortedThresholds[sortedThresholds.length - 1]?.color || '#ef4444';
        }
        
        // Draw colored cell
        const rgb = hexToRgb(cellColor);
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.rect(cellX, cellY, cellSize, cellSize, 'FD');
        
        // Draw cell border
        doc.setDrawColor(0, 0, 0);
        doc.rect(cellX, cellY, cellSize, cellSize, 'S');
      });
    });
    
    // Plot risks on matrix
    filteredRisks.forEach(risk => {
      const likelihoodValue = risk.likelihood;
      const rowIdx = matrixConfig.likelihoodLevels.length - likelihoodValue; // reverse order
      const colIdx = risk.impact - 1;
      const centerX = matrixStartX + headerSize + (colIdx * cellSize) + cellSize/2;
      const centerY = matrixStartY + headerSize + (rowIdx * cellSize) + cellSize/2;
      
      // Draw circle with white border
      doc.setFillColor(255, 255, 255);
      doc.circle(centerX, centerY, 4, 'F');
      
      // Draw colored circle based on category
      const categoryColorMap = {
        'Technical': '#3b82f6',
        'Financial': '#10b981', 
        'Resource': '#8b5cf6',
        'Scope': '#f97316',
        'External': '#ef4444',
        'Quality': '#eab308'
      };
      const circleColor = hexToRgb(categoryColorMap[risk.category] || '#3b82f6');
      doc.setFillColor(circleColor.r, circleColor.g, circleColor.b);
      doc.circle(centerX, centerY, 3, 'F');
      
      // Draw risk ID
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(6);
      doc.setFont(undefined, 'bold');
      doc.text(risk.id.toString(), centerX, centerY + 1, { align: 'center' });
    });
    
    // Add risk priority legend
    let legendY = matrixStartY + headerSize + (likelihoodLabels.length * cellSize) + 15;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Priority Levels:', matrixStartX, legendY);
    
    matrixConfig.scoreThresholds.forEach((threshold, idx) => {
      const legendX = matrixStartX + (idx % 3) * 60;
      const legendYPos = legendY + 10 + Math.floor(idx / 3) * 8;
      
      const rgb = hexToRgb(threshold.color);
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.rect(legendX, legendYPos - 2, 8, 6, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(threshold.label, legendX + 12, legendYPos + 2);
    });
    
    // Add category legend
    legendY += 35;
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Categories:', matrixStartX, legendY);
    
    const categoryColorMap = {
      'Technical': '#3b82f6',
      'Financial': '#10b981', 
      'Resource': '#8b5cf6',
      'Scope': '#f97316',
      'External': '#ef4444',
      'Quality': '#eab308'
    };
    
    Object.entries(categoryColorMap).forEach(([category, color], idx) => {
      const legendX = matrixStartX + (idx % 3) * 60;
      const legendYPos = legendY + 10 + Math.floor(idx / 3) * 8;
      
      const rgb = hexToRgb(color);
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.circle(legendX + 4, legendYPos, 3, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(category, legendX + 12, legendYPos + 2);
    });
    
    // Add summary statistics
    legendY += 35;
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Summary:', matrixStartX, legendY);
    
    const riskCounts = matrixConfig.scoreThresholds.reduce((acc, threshold) => {
      acc[threshold.label] = filteredRisks.filter(risk => {
        const priority = getRiskPriority(risk.likelihood, risk.impact);
        return priority.level === threshold.label;
      }).length;
      return acc;
    }, {});
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    let summaryY = legendY + 8;
    Object.entries(riskCounts).forEach(([level, count]) => {
      doc.text(`${level}: ${count} risks`, matrixStartX, summaryY);
      summaryY += 6;
    });
    doc.text(`Total Risks: ${filteredRisks.length}`, matrixStartX, summaryY);
    
    // --- PAGE 2: Risk Register Table ---
    doc.addPage();
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Register', 14, 20);
    
    const tableColumns = [
      'ID', 'Description', 'Initial L', 'Initial I', 'Initial Score',
      'Category', 'Action', 'Action Owner', 'Deadline', 'Status',
      'Residual L', 'Residual I', 'Residual Score'
    ];
    
    const tableRows = filteredRisks.map(risk => {
      const initialPriority = getRiskPriority(risk.initialLikelihood || risk.likelihood, risk.initialImpact || risk.impact);
      const residualPriority = getRiskPriority(risk.likelihood, risk.impact);
      return [
        risk.id,
        risk.description?.substring(0, 30) + (risk.description?.length > 30 ? '...' : ''),
        risk.initialLikelihood || risk.likelihood,
        risk.initialImpact || risk.impact,
        initialPriority.level,
        risk.category,
        risk.action,
        risk.actionOwner?.substring(0, 15) + (risk.actionOwner?.length > 15 ? '...' : ''),
        risk.deadline ? new Date(risk.deadline).toLocaleDateString() : '',
        risk.status,
        risk.likelihood,
        risk.impact,
        residualPriority.level
      ];
    });
    
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 30,
      styles: { 
        fontSize: 6,
        cellPadding: 2
      },
      headStyles: { 
        fillColor: [41, 128, 185],
        fontSize: 7
      },
      columnStyles: {
        1: { cellWidth: 25 }, // Description
        6: { cellWidth: 15 }, // Action
        7: { cellWidth: 15 }, // Action Owner
      },
      margin: { left: 6, right: 6 }
    });
    
    doc.save('risk-register.pdf');
  };

  // ================= Spreadsheet Import / Template ====================

  const importFromSpreadsheet = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const mapped = json.map((row, idx) => ({
        id: idx + 1,
        description: row.Description || '',
        initialLikelihood: Number(row['Initial L']) || 1,
        initialImpact: Number(row['Initial I']) || 1,
        likelihood: Number(row['Residual L']) || 1,
        impact: Number(row['Residual I']) || 1,
        category: row.Category || '',
        action: row.Action || '',
        actionDescription: row['Action Description'] || '',
        actionOwner: row['Action Owner'] || '',
        deadline: row.Deadline || '',
        status: row.Status || ''
      }));
      setRisks(mapped);
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const headers = [
      'Description','Initial L','Initial I','Category','Action','Action Description','Action Owner','Deadline','Status','Residual L','Residual I'
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'risk-register-template.xlsx');
  };

  // Ensure backward alias
  const matrixWithAlias = {
    ...matrixConfig,
    probabilityLevels: matrixConfig.probabilityLevels || matrixConfig.likelihoodLevels || [],
    likelihoodLevels: matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || []
  };

  const value = {
    // State
    risks,
    setRisks,
    editingRisk,
    setEditingRisk,
    showRiskDetail,
    setShowRiskDetail,
    filters,
    setFilters,
    matrixConfig: matrixWithAlias,
    setMatrixConfig,
    kanbanGrouping,
    setKanbanGrouping,
    editingCell,
    setEditingCell,
    editingValue,
    setEditingValue,
    sortConfig,
    setSortConfig,
    sampleDataSet,
    setSampleDataSet,
    draggedRisk,
    setDraggedRisk,
    dragOverColumn,
    setDragOverColumn,
    
    // Categories state (CRUD capable)
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    
    // Constants
    riskActions,
    statusOptions,
    actionColors,
    categoryColors,
    kanbanFieldMap,
    
    // Computed
    filteredRisks,
    getSortedRisks,
    
    // Functions
    updateRisk,
    removeRisk,
    getRiskPriority,
    clearFilters,
    getUniqueOwners,
    getKanbanColumns,
    openRiskDetail,
    closeRiskDetail,
    handleEditRiskChange,
    saveRiskChanges,
    handleSort,
    getSortIcon,
    startEditing,
    saveInlineEdit,
    cancelInlineEdit,
    handleInlineKeyPress,
    loadSampleData,
    clearAllData,
    
    // CRUD for Actions
    addAction,
    updateAction,
    deleteAction,
    
    // CRUD for Status
    addStatus,
    updateStatus,
    deleteStatus,

    // Add Risk modal
    handleAddRiskModal,

    // Export helpers
    exportToSpreadsheet,
    exportToPDF,
    importFromSpreadsheet,
    downloadTemplate
  };

  return <RiskContext.Provider value={value}>{children}</RiskContext.Provider>;
}

export function useRiskContext() {
  const ctx = useContext(RiskContext);
  if (!ctx) throw new Error('useRiskContext must be used within a RiskProvider');
  return ctx;
}

export default RiskContext; 